import React, { useState, useEffect } from 'react';
import { Send, CheckCircle2, ShieldCheck, AlertCircle, Clock, Phone, Globe, User, HelpCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { submitNRICallbackLead } from '../services/api';
import { trackCallbackFormStart, trackLeadGenerated } from '../services/analytics';

const COUNTRY_CODES = [
  { code: '+1', country: 'USA / Canada' },
  { code: '+44', country: 'United Kingdom' },
  { code: '+61', country: 'Australia' },
  { code: '+971', country: 'UAE' },
  { code: '+65', country: 'Singapore' },
  { code: '+49', country: 'Germany' },
  { code: '+966', country: 'Saudi Arabia' },
  { code: '+974', country: 'Qatar' },
  { code: '+965', country: 'Kuwait' },
  { code: '+91', country: 'India' },
];

const COUNTRIES = [
  'United States',
  'Canada',
  'United Kingdom',
  'Australia',
  'United Arab Emirates',
  'Singapore',
  'Germany',
  'Saudi Arabia',
  'Qatar',
  'Kuwait',
  'New Zealand',
  'Ireland',
  'Other Country',
];

const INTEREST_OPTIONS = [
  'Health Insurance for Parents',
  'Health Insurance for Myself',
  'Life Insurance for Myself',
  'Life Insurance for Family',
  'Retirement / Pension Plans',
  'Not Sure / Need Guidance',
];

const TIME_OPTIONS = [
  'Morning (9 AM - 12 PM IST)',
  'Afternoon (12 PM - 4 PM IST)',
  'Evening (4 PM - 8 PM IST)',
  'Any time that suits',
];

const CallbackForm = ({ source = 'nri_blog_embedded', onSuccess, isModal = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    country_code: '+1',
    country: 'United States',
    insurance_interest: 'Health Insurance for Parents',
    preferred_time: 'Morning (9 AM - 12 PM IST)',
    message: '',
    consent: false,
    website_hp: '', // Honeypot
  });

  const [hasStarted, setHasStarted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedLead, setSubmittedLead] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  // Extract UTM parameters on mount
  const [utmParams, setUtmParams] = useState({
    utm_source: 'instagram',
    utm_medium: 'auto_dm',
    utm_campaign: 'nri_insurance_2026',
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      setUtmParams({
        utm_source: urlParams.get('utm_source') || 'instagram',
        utm_medium: urlParams.get('utm_medium') || 'auto_dm',
        utm_campaign: urlParams.get('utm_campaign') || 'nri_insurance_2026',
      });
    }
  }, []);

  const handleFieldChange = (field, value) => {
    if (!hasStarted) {
      setHasStarted(true);
      trackCallbackFormStart(isModal ? 'modal' : 'embedded');
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrorMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    // Honeypot check
    if (formData.website_hp && formData.website_hp.trim()) {
      setSubmittedLead({ lead_id: 'VSR-OK' });
      return;
    }

    // Client-side validation
    if (!formData.name.trim() || formData.name.trim().length < 2) {
      setErrorMessage('Please enter your full name (minimum 2 characters).');
      return;
    }

    const cleanDigits = formData.phone.replace(/\D/g, '');
    if (cleanDigits.length < 6) {
      setErrorMessage('Please enter a valid phone or WhatsApp number with at least 6 digits.');
      return;
    }

    if (!formData.consent) {
      setErrorMessage('Please check the consent box to allow VSR Insurance Plus to contact you.');
      return;
    }

    setIsSubmitting(true);

    const payload = {
      name: formData.name.trim(),
      phone: `${formData.country_code} ${formData.phone.trim()}`,
      country_code: formData.country_code,
      country: formData.country,
      insurance_interest: formData.insurance_interest,
      preferred_time: formData.preferred_time,
      message: formData.message.trim(),
      consent: formData.consent,
      website_hp: formData.website_hp,
      lead_source: 'Website Blog',
      source_page: typeof window !== 'undefined' ? window.location.pathname : '/blog/best-insurance-for-nris-and-parents',
      utm_source: utmParams.utm_source,
      utm_medium: utmParams.utm_medium,
      utm_campaign: utmParams.utm_campaign,
    };

    try {
      const result = await submitNRICallbackLead(payload);
      setIsSubmitting(false);
      setSubmittedLead(result);
      trackLeadGenerated(formData.insurance_interest, formData.country);

      if (typeof onSuccess === 'function') {
        onSuccess(result);
      }
    } catch (err) {
      setIsSubmitting(false);
      const detail = err.response?.data?.detail || 'Unable to record your request at this moment. Please call or WhatsApp V. S. Reddy directly at +91-9849664800.';
      setErrorMessage(detail);
    }
  };

  if (submittedLead) {
    return (
      <div className="bg-white dark:bg-slate-900 rounded-3xl p-8 md:p-10 border border-emerald-200 dark:border-emerald-900/40 shadow-xl text-center space-y-5 animate-in fade-in duration-300">
        <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-full flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-10 h-10" />
        </div>

        <h3 className="text-2xl md:text-3xl font-extrabold text-slate-900 dark:text-white">
          Thank You! Your Request Has Been Received
        </h3>

        <p className="text-slate-600 dark:text-slate-300 text-base max-w-lg mx-auto leading-relaxed">
          VSR Insurance Plus will contact you using the details provided. Senior Advisor V. S. Reddy personally reviews your family requirements before calling.
        </p>

        {submittedLead.lead_id && (
          <div className="inline-block bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-mono text-xs px-4 py-2 rounded-xl">
            Reference ID: <strong>{submittedLead.lead_id}</strong>
          </div>
        )}

        <div className="pt-4 border-t border-slate-100 dark:border-slate-800 text-xs text-slate-400">
          Need immediate assistance? You may also WhatsApp directly: <a href="https://wa.me/919849664800" target="_blank" rel="noopener noreferrer" className="text-primary font-bold hover:underline">+91-9849664800</a>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5" noValidate>
      {errorMessage && (
        <div className="p-4 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900 rounded-2xl flex items-start gap-3 text-rose-800 dark:text-rose-300 text-sm">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <span>{errorMessage}</span>
        </div>
      )}

      {/* Honeypot hidden input for spam bots */}
      <div style={{ display: 'none', position: 'absolute', left: '-9999px' }} aria-hidden="true">
        <label htmlFor="website_hp_field">Leave this empty</label>
        <input
          id="website_hp_field"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={formData.website_hp}
          onChange={(e) => setFormData({ ...formData, website_hp: e.target.value })}
        />
      </div>

      {/* Full Name */}
      <div className="space-y-1.5">
        <label htmlFor="nri-lead-name" className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
          <User className="w-3.5 h-3.5 text-primary" /> Full Name <span className="text-rose-500">*</span>
        </label>
        <input
          id="nri-lead-name"
          type="text"
          required
          value={formData.name}
          onChange={(e) => handleFieldChange('name', e.target.value)}
          placeholder="e.g. Ramesh Kumar"
          className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary text-sm"
        />
      </div>

      {/* Phone with Country Code */}
      <div className="space-y-1.5">
        <label htmlFor="nri-lead-phone" className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
          <Phone className="w-3.5 h-3.5 text-primary" /> Phone / WhatsApp Number <span className="text-rose-500">*</span>
        </label>
        <div className="grid grid-cols-12 gap-2">
          <div className="col-span-5 sm:col-span-4">
            <select
              aria-label="Country Calling Code"
              value={formData.country_code}
              onChange={(e) => handleFieldChange('country_code', e.target.value)}
              className="w-full px-2.5 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {COUNTRY_CODES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.code} ({c.country})
                </option>
              ))}
            </select>
          </div>
          <div className="col-span-7 sm:col-span-8">
            <input
              id="nri-lead-phone"
              type="tel"
              required
              value={formData.phone}
              onChange={(e) => handleFieldChange('phone', e.target.value)}
              placeholder="408 555 0199"
              className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary text-sm font-medium"
            />
          </div>
        </div>
      </div>

      {/* Country of Residence & Insurance Interest */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <label htmlFor="nri-lead-country" className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
            <Globe className="w-3.5 h-3.5 text-primary" /> Country of Residence <span className="text-rose-500">*</span>
          </label>
          <select
            id="nri-lead-country"
            required
            value={formData.country}
            onChange={(e) => handleFieldChange('country', e.target.value)}
            className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {COUNTRIES.map((cty) => (
              <option key={cty} value={cty}>
                {cty}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-1.5">
          <label htmlFor="nri-lead-interest" className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
            <HelpCircle className="w-3.5 h-3.5 text-primary" /> Primary Interest <span className="text-rose-500">*</span>
          </label>
          <select
            id="nri-lead-interest"
            required
            value={formData.insurance_interest}
            onChange={(e) => handleFieldChange('insurance_interest', e.target.value)}
            className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {INTEREST_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Preferred Callback Time */}
      <div className="space-y-1.5">
        <label htmlFor="nri-lead-time" className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5 text-primary" /> Preferred Callback Time (Optional)
        </label>
        <select
          id="nri-lead-time"
          value={formData.preferred_time}
          onChange={(e) => handleFieldChange('preferred_time', e.target.value)}
          className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          {TIME_OPTIONS.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      {/* Additional Message */}
      <div className="space-y-1.5">
        <label htmlFor="nri-lead-message" className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">
          Additional Notes or Questions (Optional)
        </label>
        <textarea
          id="nri-lead-message"
          rows={3}
          value={formData.message}
          onChange={(e) => handleFieldChange('message', e.target.value)}
          placeholder="e.g. Parents are 58 & 55 in Hyderabad with mild diabetes. Need 4X floater advice..."
          className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary text-sm resize-none"
        />
      </div>

      {/* Required Consent Checkbox (Strictly Unchecked by Default) */}
      <div className="pt-2">
        <label className="flex items-start gap-3 cursor-pointer select-none">
          <input
            type="checkbox"
            required
            checked={formData.consent}
            onChange={(e) => handleFieldChange('consent', e.target.checked)}
            className="w-4 h-4 mt-1 rounded text-primary focus:ring-primary border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 flex-shrink-0"
          />
          <span className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
            I agree to be contacted by VSR Insurance Plus regarding my insurance inquiry using the contact details I provide. I have read the{' '}
            <Link to="/privacy" target="_blank" className="text-primary underline font-medium hover:text-blue-700">
              Privacy Policy
            </Link>
            . <span className="text-rose-500">*</span>
          </span>
        </label>
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full py-4 px-6 bg-primary hover:bg-blue-600 text-white rounded-2xl font-extrabold text-base flex items-center justify-center gap-2 shadow-lg shadow-primary/20 transition-all hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
      >
        {isSubmitting ? (
          <>
            <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            <span>Submitting Callback Request...</span>
          </>
        ) : (
          <>
            <span>Request a Callback</span>
            <Send className="w-4 h-4" />
          </>
        )}
      </button>

      <div className="flex items-center justify-center gap-2 text-[11px] text-slate-400 pt-1">
        <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
        <span>100% Confidential. Zero spam. Directly reviewed by V. S. Reddy.</span>
      </div>
    </form>
  );
};

export default CallbackForm;
